import { AbsoluteCenter, Box, Button, Spinner, Text, VStack } from '@chakra-ui/react';
import { useTimeout } from 'usehooks-ts';
import { Navigate, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useToastStore } from '@/client/services/state/toastStore';
import { useTranslation } from 'react-i18next';
import ContinuInsightsService from '@/client/services/api/admin/continu-insights/ContinuInsightsService';
import { useAccessHelper } from '@/client/services/hooks/auth/useAccessHelper';
import { useRef } from 'react';

function UnsuccessfulUnsubscribe() {
  const { t } = useTranslation();
  return (
    <Box
      backgroundColor="white"
      border="1px solid"
      borderColor="neutral.100"
      borderRadius="24px"
      paddingY={16}
      paddingX={20}
      boxShadow="5px 5px 11px -6px rgba(0,0,0,0.2)"
    >
      <VStack>
        <Text fontSize="18px" fontWeight={500}>
          {t('continuInsights.unsubscribe.error')}
        </Text>
        <Text>{t('continuInsights.unsubscribe.errorFull.body')} </Text>
      </VStack>
    </Box>
  );
}

function LandingPage({ mutate }: { mutate: () => void }) {
  const { t } = useTranslation();
  const buttonRef = useRef<HTMLButtonElement>(null);

  useTimeout(() => buttonRef.current?.click(), 200);

  return (
    <VStack spacing={6}>
      <Spinner size="lg" color="neutral.300" />

      <Text fontSize="18px" fontWeight={500}>
        {t('continuInsights.unsubscribing')}
      </Text>

      <Button ref={buttonRef} onClick={() => mutate()} hidden />
    </VStack>
  );
}

// Landing page for a user who is unsubscribing from Continu Insights
export default function ContinuInsightsUnsubscribe() {
  const { t } = useTranslation();
  const { setToast } = useToastStore();
  const { allowance } = useAccessHelper();
  const navigate = useNavigate();

  const { mutateAsync: unsubscribeAsync, isError } = useMutation({
    mutationFn: () => ContinuInsightsService.unsubscribeUser(),
    onSuccess: () => {
      setToast({
        show: true,
        status: 'success',
        title: t('continuInsights.unsubscribe.success'),
      });
      navigate('/explore', { replace: true });
    },
    onError: () =>
      setToast({
        show: true,
        status: 'error',
        title: t('continuInsights.unsubscribe.error'),
      }),
  });

  if (!allowance.continuInsights) {
    return <Navigate to="/explore" replace />;
  }

  return (
    <Box height="100vh">
      <AbsoluteCenter>
        <LandingPage
          mutate={() => {
            unsubscribeAsync();
          }}
        />
        {isError && <UnsuccessfulUnsubscribe />}
      </AbsoluteCenter>
    </Box>
  );
}
